import { toast } from 'react-toastify';
import { displayQuote } from '../../../utils/symbolQuotes';
import { flushSync } from 'react-dom';
import NoRecordsMessage from '../../utils/NoRecordsMessage';
import React, { useEffect, useLayoutEffect, useState, useRef, useCallback, useMemo } from 'react'
import PanelSkeleton from '../../utils/PanelSkeleton'
import { FaSearch, FaTimes } from 'react-icons/fa';
import { WS_ENDPOINT_PLATFORM_LIVE_FEEDS, WS_ENDPOINT_SYMBOLS_LIVE_FEEDS } from "../../../data/Endpoints-WS";
import { ws_create } from "../../../data/websocket/Websocket-Middleware";
import { useSymbolContext } from "../../../contexts/Symbol-Context";
import { useAuthContext } from '../../../contexts/Auth-Context';
import { useChartContext } from '../../../contexts/Chart-Context';
import { API_ENDPOINT_SYMBOLS } from '../../../data/Endpoints-API';
import { API_ENDPOINT_USER_WATCHLIST } from '../../../data/Endpoints-API';
import { API_ENDPOINT_INSERT_WATCHLIST } from '../../../data/Endpoints-API';
import { API_ENDPOINT_DELETE_WATCHLIST } from '../../../data/Endpoints-API';
import './Symbol.scss'
import APIMiddleware from '../../../data/api/Api-Middleware';
import Select from 'react-select';
import { Ripple } from 'react-ripple-click';
import 'react-ripple-click/dist/index.css';
import StarSvg from './StarSvg';
import { VariableSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { translateCategoryName } from '../../../i18n/categoryLabel';
import { useTranslation } from 'react-i18next';
import TabIndicator from '../../utils/TabIndicator.jsx';

// Stable component identity preserves the hovered/pressed DOM row across price ticks.
function StableSymbolRow({ data, ...row }) {
    return data(row);
}

const categoryMotion = { duration: 280, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'both' };
function animateCategoryClick(update) {
    return () => update();
}

function AnimatedSymbolGroup({ open, children }) {
    const nodeRef = useRef(null);
    const firstLayout = useRef(true);
    const [mounted, setMounted] = useState(open);
    const targetHeight = Number(children.props.style.height) || 0;
    useLayoutEffect(() => {
        const node = nodeRef.current;
        const from = parseFloat(getComputedStyle(node).height) || 0;
        const to = open ? targetHeight : 0;
        const initial = firstLayout.current;
        firstLayout.current = false;
        if (open) setMounted(true);
        node.style.height = to + 'px';
        if (initial || Math.abs(from - to) < 0.5 ||
            window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            if (!open) setMounted(false);
            return;
        }
        // The clipping boundary and layout slot are the SAME animated box.
        // Rows can never spill into the next category, including search resets.
        const animation = node.animate([{ height: from + 'px' }, { height: to + 'px' }], categoryMotion);
        let active = true;
        animation.finished.then(() => {
            if (!active) return;
            animation.cancel();
            if (!open) setMounted(false);
        }, () => {});
        return () => {
            active = false;
            // A rapid toggle starts at the current visible height, not an old endpoint.
            node.style.height = getComputedStyle(node).height;
            animation.cancel();
        };
    }, [open, targetHeight]);
    return <div ref={nodeRef} className="symbol-category-reveal" aria-hidden={!open}>
        <div className="symbol-category-clip">{(open || mounted) ? children : null}</div>
    </div>;
}

// Quote spread in price units, rounded to the precision of the input quotes.
function formatSpread(quote) {
    if (quote?.ask == null || quote?.bid == null || quote.ask === '' || quote.bid === '') return '-';
    const ask = Number(quote.ask), bid = Number(quote.bid);
    if (!Number.isFinite(ask) || !Number.isFinite(bid)) return '-';
    const precision = value => {
        const [mantissa, exponent = '0'] = String(value).toLowerCase().split('e');
        return Math.max(0, (mantissa.split('.')[1]?.length || 0) - Number(exponent));
    };
    return (ask - bid).toFixed(Math.min(20, Math.max(precision(quote.ask), precision(quote.bid))));
}

const handleCategoryKeyDown = event => {
                                        if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); event.currentTarget.click(); }
                                        if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
                                            event.preventDefault();
                                            const headings = [...event.currentTarget.closest('.Symbol-list').querySelectorAll('heading[role="button"]')];
                                            const index = headings.indexOf(event.currentTarget);
                                            const next = event.key === 'Home' ? 0 : event.key === 'End' ? headings.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + headings.length) % headings.length;
                                            headings[next]?.focus();
                                        }
                                    };

const Symbol = () => {
    const { t } = useTranslation();

    //Tabs
    const [activeMode, setActiveMode] = useState('symbols');
    const [symbolCategory,setSymbolCategory] = useState(() => {
        const symbolCategoryLocal = localStorage.getItem(`symbolCategory`);
        return symbolCategoryLocal ? symbolCategoryLocal: "Forex";
    });
    const [symbolCategoryId,setSymbolCategoryId] = useState(() => {
        const symbolCategoryIdLocal = localStorage.getItem(`symbolCategoryID`);
        return symbolCategoryIdLocal ? symbolCategoryIdLocal: "1";
    });
    // Track which categories are open (for WatchLists to show all)
    const [openCategories, setOpenCategories] = useState(new Set());




    //start click
    const [watchlistSymbols, setWatchlistSymbols] = useState({});


    const watchlistPendingRef = useRef(new Set());
    const handleTdClick = async (symbol) => {
        if (watchlistPendingRef.current.has(symbol)) return;
        watchlistPendingRef.current.add(symbol);
        const previous = !!watchlistSymbols[symbol];
        setWatchlistSymbols(state => ({...state, [symbol]: !previous}));
        try {
            const saved = previous ? await deleteWatchlist(symbol) : await insertWatchlist(symbol);
            if (!saved) {
                setWatchlistSymbols(state => ({...state, [symbol]: previous}));
                toast.error(t('symbol.watchlistSaveFailed', 'Could not update watchlist. Please try again.'));
            }
        } finally {
            watchlistPendingRef.current.delete(symbol);
        }
    };

    const starBoolean = (e) => {
        if (e.target.tagName == 'path') {
            if (e.target.parentNode.getAttribute('bool') == 'false') {
                e.target.parentNode.setAttribute('bool', true);
            } else {
                e.target.parentNode.setAttribute('bool', false);
            }
        } else {
            if (e.target.children[0].getAttribute('bool') == 'false') {
                e.target.children[0].setAttribute('bool', true);
            } else {
                e.target.children[0].setAttribute('bool', false);
            }
        }
    }

        //CONTEXT
    const { loadingSymbolContext, symbolData, symbolNames,  updateSymbolData, loadAllSymbolsData, updateSymbolDetailsData, symbolDetails, symbolWithCategories, symbolCategories, setSelectedCategoryId, allSymbolOptions,selectedSymbolSession, setSelectedSymbolSession } = useSymbolContext();
    const { user, platFromData, sendDataToServer, selectedAuthSymbol, setAuthSelectedSymbol,setAuthSelectedCategory,authSelectedCategory,selectedAuthSymbolId,setSelectedAuthSymbolId ,isWebSocketRefetch} = useAuthContext();
    const { selectedTimeFrame, selectedStyle } = useChartContext();

    //everything must be initialize before further processing, so block everything base on this flag state
    const [isInitialized, setIsInitialized] = useState(false);

    const [isMobile, setIsMobile] = useState(window.innerWidth <= 960);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 960);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);


    const [searchInput, setSearchInput] = useState('');
    const [keyboardSymbol, setKeyboardSymbol] = useState(null);
    const [pendingSymbol, setPendingSymbol] = useState(null);
    const virtualLists = useRef(new Map());
    useEffect(() => {
        const ready = event => setPendingSymbol(current => current === event.detail?.symbol ? null : current);
        window.addEventListener('symbol-chart-ready', ready);
        return () => window.removeEventListener('symbol-chart-ready', ready);
    }, []);
    useEffect(() => {
        if (!pendingSymbol) return;
        const timeout = setTimeout(() => setPendingSymbol(null), 15000);
        return () => clearTimeout(timeout);
    }, [pendingSymbol]);
    const [closedSearchCategories, setClosedSearchCategories] = useState(new Set());
    const toggleSearchCategory = (name) => {
        setClosedSearchCategories(previous => {
            const next = new Set(previous);
            if (next.has(name)) next.delete(name); else next.add(name);
            return next;
        });
    };
    // Replace the localStorage-based initialization with simple state
    const [paginationStart, setPaginationStart] = useState(0);
    const [paginationLimit] = useState(100); // Fixed limit of 100 items per page
    const [totalSymbols, setIsTotalSymbols] =  useState();
    const getDescriptionTooltip = useCallback((description) => {
        if (!description) return '';
        const maxChars = isMobile ? 28 : 54;
        return description.length > maxChars ? description : '';
    }, [isMobile]);

    const debounceRef = useRef(null);

    const listRef = useRef(null);
    const isFetchingRef = useRef(false);
    const categoryInitializedRef = useRef(false);

    const SYMBOL_ROW_HEIGHT_WITH_DESC = 44;
    const SYMBOL_ROW_HEIGHT_NO_DESC = 30;

    const hasSymbolDescription = useCallback((data) => {
        const desc = data?.description;
        return typeof desc === 'string' && desc.trim().length > 0;
    }, []);

    const getSymbolRowHeight = useCallback((data) => {
        return hasSymbolDescription(data) ? SYMBOL_ROW_HEIGHT_WITH_DESC : SYMBOL_ROW_HEIGHT_NO_DESC;
    }, [hasSymbolDescription]);

    const getVirtualListHeight = useCallback((entries) => {
        const maxHeight = Math.max(220, Math.round(window.innerHeight * 0.45));
        const list = entries || [];
        const contentHeight = list.reduce((sum, [, d]) => sum + getSymbolRowHeight(d), 0);
        return Math.min(maxHeight, Math.max(SYMBOL_ROW_HEIGHT_WITH_DESC, contentHeight));
    }, [getSymbolRowHeight]);


    useEffect(() => {
      if (listRef.current) listRef.current.scrollTop = 0;
    }, [symbolCategoryId]);

    useEffect(() => {
      if (activeMode === 'watchlists') {
        sendDataToServer({
          type: 'watchlist',
          watchlistSymbols: Object.keys(watchlistSymbols).filter((name) => watchlistSymbols[name]),
        });
        return;
      }
      if (symbolCategoryId == null) return;
      sendDataToServer({
        type: 'SymbolsPagination',
        paginationStart: 0,
        paginationEnd: 100,
        category: symbolCategoryId,
      });
    }, [symbolCategoryId, isWebSocketRefetch, activeMode]);



    const changeSymbolCategory = (e, o) => {
        if (o === false) {
            const categories = symbolCategories?.find((category) => category.name === e);
            if (!categories) return; // Guard against undefined category

            // Update state immediately
            setSymbolCategory(e);
            setSymbolCategoryId(categories.id);

            // Save category ID to localStorage when in 'All Symbols' mode
            if (activeMode === 'symbols') {
                localStorage.setItem('symbolCategory', e);
                localStorage.setItem('symbolCategoryID', categories.id.toString());
            }

            setIsTotalSymbols(symbolWithCategories.filter((symbol) =>
                symbol.symbol_category === categories.id
            ).length);

            // Reset pagination when changing category
            setPaginationStart(0);
        } else {
            setSymbolCategory(null);
        }
    };

    // Handle category toggle for WatchLists
    const toggleWatchlistCategory = (categoryName) => {
        setOpenCategories(prev => {
            const newSet = new Set(prev);
            if (newSet.has(categoryName)) {
                newSet.delete(categoryName);
            } else {
                newSet.add(categoryName);
            }
            return newSet;
        });
    };

    useEffect(() => () => clearTimeout(debounceRef.current), []);
    const handleSearchChange = useCallback((value) => {
        setSearchInput(value);
        setKeyboardSymbol(null);
        setClosedSearchCategories(new Set());
        clearTimeout(debounceRef.current);
        if (!value.length) {
            sendDataToServer(activeMode === 'watchlists' ? {
                type: 'watchlist',
                watchlistSymbols: Object.keys(watchlistSymbols).filter(name => watchlistSymbols[name]),
            } : {
                type: 'SymbolsPagination', paginationStart: 0, paginationEnd: 100, category: symbolCategoryId,
            });
            return;
        }
        debounceRef.current = setTimeout(() => {
            sendDataToServer({type: 'SearchSymbols', searchInput: value});
        }, 500);
    }, [symbolCategoryId, activeMode, watchlistSymbols, sendDataToServer]);

    //create web-socket instance for the component
    const ws_symbol = ws_create();
    const WS_MESSAGE_EVENT_LISTENER = "ws_message_platform";

    const [watchlistData, setWatchlistData] = useState([]);
    let isSymbolDetailsUpdated = false;

    useEffect(() => {
        if (selectedAuthSymbol != '') {
            localStorage.setItem('prevSymbol', localStorage.symbol);
            localStorage.setItem('symbol', selectedAuthSymbol);
            localStorage.setItem('category',authSelectedCategory);
        }
    }, [selectedAuthSymbol])

    useEffect(() => {
      if(selectedAuthSymbolId != ''){
            localStorage.setItem('symbol_id', selectedAuthSymbolId);
        }
    }, [selectedAuthSymbolId])
    useEffect(() => {
        if (user && user.userId != undefined && user.userId > 0) {
            //nothing will load in this component until SymbolContext fully loaded
            if (!loadingSymbolContext) {
                //everything must be initialize before further processing, so block everything base on this flag state
                if (!isInitialized) {
                    const getSymbols = () => {
                        try {
                            const initialSymbolData = {};
                            const savedStart = parseInt(localStorage.getItem(`paginationStart_${symbolCategory}`)) || 0;
                            const savedLimit = parseInt(localStorage.getItem(`paginationLimit_${symbolCategory}`)) || 10;
                            setIsTotalSymbols( symbolWithCategories.filter((symbol)=>symbol.symbol_category==symbolCategoryId).length);

                            symbolWithCategories.forEach((symbol) => {

                                initialSymbolData[symbol.name] = {id:symbol.id, ask: symbol.initial_ask_price, bid: symbol.initial_bid_price, spread: null, ask_class: 'Symbol-price-same', bid_class: 'Symbol-price-same', symbol_category: symbol.symbol_category, is_session_active: symbol.is_session_active, description: symbol.description || null, asset_class_id: symbol.asset_class_id };
                                });
                            //set symbol initial list data
                            loadAllSymbolsData(initialSymbolData);

                            if (localStorage.symbol == null) {
                                updateSymbolDetailsData(symbolNames[0]);
                            } else {
                                updateSymbolDetailsData(localStorage.symbol);
                            }

                            //everything must be initialize before further processing, so block everything base on this flag state
                            setIsInitialized(true);

                            getWatchlistdata();

                        } catch (error) {
                            // Handle API request error
                            console.error(`API request error: ${API_ENDPOINT_SYMBOLS}`, error);
                        }
                    }

                    //load symbols in list
                    getSymbols();
                }

                //once everything initialized then further process base on this flag state
                if (isInitialized && platFromData.length > 0  && platFromData[0] != undefined && platFromData[0] != 'undefined'&& Object.keys(platFromData[0]).length > 0) {
                    //update list with updated live feed
                    const updateLiveFeedData = () => {
                        const updatedSymbolData = platFromData[0];
                        loadAllSymbolsData(updatedSymbolData);
                    };
                    updateLiveFeedData();

                    return async () => {
                    };
                }
            }
        }

    }, [loadingSymbolContext, isInitialized, selectedAuthSymbol, user, selectedTimeFrame,symbolCategory, platFromData[0]]);

    useEffect(() => {
        // Only set initial category once when categories are first loaded
        if (symbolCategories?.length && !categoryInitializedRef.current) {
            const savedCategory = localStorage.getItem('symbolCategory');
            const savedCategoryExists = savedCategory && symbolCategories.find(cat => cat.name === savedCategory);

            if (savedCategoryExists) {
                // Restore saved category
                const category = symbolCategories.find(cat => cat.name === savedCategory);
                setSymbolCategory(savedCategory);
                setSymbolCategoryId(category.id.toString());
            } else if (symbolCategories.length > 0) {
                // Set to first category
                setSymbolCategory(symbolCategories[0].name);
                setSymbolCategoryId(symbolCategories[0].id.toString());
                localStorage.setItem('symbolCategory', symbolCategories[0].name);
                localStorage.setItem('symbolCategoryID', symbolCategories[0].id.toString());
            }
            categoryInitializedRef.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [symbolCategories?.length]);

    // Selected quotes have their own stream; never wait for a list/position packet.
    useEffect(() => {
        const quote = platFromData[8]?.symbol;
        if (quote?.name === selectedAuthSymbol) {
            updateSymbolData(quote.name, quote.ea, quote.eb, quote.bid, quote.ask, quote.is_session_active, false);
        }
    }, [platFromData[8], selectedAuthSymbol]);
    const getWatchlistdata = async () => {
        try {
            // Use the API_ENDPOINT_USER_WATCHLIST in the fetch request
            const response = await APIMiddleware.get(API_ENDPOINT_USER_WATCHLIST(user.userId));

            if (response.data) {
                const symbols = response.data;
                setWatchlistSymbols(() => {
                    // Use reduce to iterate over the symbols array and create an object with true values
                    const updatedSymbols = symbols.reduce((acc, symbol) => {
                        acc[symbol.name] = true;
                        return acc;
                    }, {});

                    return updatedSymbols;
                });

            }
        } catch (error) {
            console.error('Error fetching watchlist data:', error);
        }
    };

    const insertWatchlist = async (symbol) => {
        try {
            // Call the API endpoint that triggers the stored procedure
            await APIMiddleware.post(API_ENDPOINT_INSERT_WATCHLIST(user.userId, symbol));
            return true;
        } catch (error) {
            console.error('Error inserting watchlist item:', error);
            return false;
        }
    };

    const deleteWatchlist = async (symbol) => {
        try {
            // Call the API endpoint that triggers the stored procedure for deletion
            await APIMiddleware.post(API_ENDPOINT_DELETE_WATCHLIST(user.userId, symbol));
            return true;
        } catch (error) {
            console.error('Error deleting watchlist item:', error);
            return false;
        }
    };

    // Filter symbols based on the search input
    const filteredSymbols = useMemo(() => {
        const query = searchInput.trim().toLowerCase();
        return Object.keys(symbolData).filter(symbol => {
            const description = symbolData[symbol]?.description?.toLowerCase?.() || '';
            return symbol.toLowerCase().includes(query) || description.includes(query);
        });
    }, [symbolData, searchInput]);
    // Price updates must not repeatedly scan the entire search result for each row.
    const filteredSymbolSet = useMemo(() => new Set(filteredSymbols), [filteredSymbols]);
    const categoryEntries = useMemo(() => {
        const grouped = new Map();
        for (const entry of Object.entries(symbolData)) {
            if (!filteredSymbolSet.has(entry[0])) continue;
            const category = String(entry[1].symbol_category);
            if (!grouped.has(category)) grouped.set(category, []);
            grouped.get(category).push(entry);
        }
        return grouped;
    }, [symbolData, filteredSymbolSet]);

    //handle symbols list row clicks
    const handleRowClick = (symbolName,symbolData) => {
        if (!symbolData[symbolName]) return;
        if (symbolName !== selectedAuthSymbol) setPendingSymbol(symbolName);
        // Persist the explicit choice before any catalogue refresh can restore it.
        localStorage.setItem('symbol', symbolName);
        // Update symbol context data when a row is clicked
        updateSymbolData(symbolName, symbolData[symbolName].ea, symbolData[symbolName].eb, symbolData[symbolName].bid, symbolData[symbolName].ask, symbolData[symbolName].is_session_active);
        setSelectedCategoryId(symbolData[symbolName].symbol_category);
        setAuthSelectedCategory(symbolData[symbolName].symbol_category);
        setAuthSelectedSymbol(symbolName);

        setSelectedAuthSymbolId(symbolData[symbolName]?.id);
    };

    const handleSearchKeyDown = event => {
        if (event.key === 'Escape') { event.preventDefault(); handleSearchChange(''); return; }
        if (event.key === 'Enter' && keyboardSymbol) { event.preventDefault(); handleRowClick(keyboardSymbol, symbolData); return; }
        if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
        const matches = (symbolCategories || []).flatMap(category => filteredSymbols.filter(name =>
            Number(symbolData[name]?.symbol_category) === Number(category.id) &&
            (activeMode !== 'watchlists' || watchlistSymbols[name])));
        if (!matches.length) return;
        event.preventDefault();
        const index = matches.indexOf(keyboardSymbol);
        const next = matches[index < 0 ? (event.key === 'ArrowDown' ? 0 : matches.length - 1) : Math.max(0, Math.min(matches.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1)))];
        setKeyboardSymbol(next);
        const category = symbolCategories.find(c => Number(c.id) === Number(symbolData[next]?.symbol_category));
        if (!category) return;
        setClosedSearchCategories(previous => { const updated = new Set(previous); updated.delete(category.name); return updated; });
        if (!searchInput) {
            if (activeMode === 'watchlists') setOpenCategories(previous => new Set([...previous, category.name]));
            else changeSymbolCategory(category.name, false);
        }
        requestAnimationFrame(() => {
            const rows = matches.filter(name => Number(symbolData[name]?.symbol_category) === Number(category.id));
            virtualLists.current.get(category.id)?.scrollToItem(rows.indexOf(next), 'smart');
        });
    };

    const SymbolPanelClass = activeMode === 'symbols' ? 'visible' : 'hidden';
    const WatchListClass = activeMode === 'watchlists' ? 'visible' : 'hidden';


    const handleModeClick = (mode) => {
        clearTimeout(debounceRef.current);
        setActiveMode(mode);
        setSearchInput("");
        if (mode === 'watchlists') {
            // Only open categories that actually have watchlist symbols
            const watchlistCategoryNames = new Set();
            Object.entries(symbolData || {}).forEach(([sym, data]) => {
                if (!watchlistSymbols[sym]) return;
                const cat = symbolCategories?.find((c) => Number(c.id) === Number(data?.symbol_category));
                if (cat?.name) watchlistCategoryNames.add(cat.name);
            });
            setOpenCategories(watchlistCategoryNames);
            setSymbolCategory(null); // Clear single category selection

            const watchlistSymbolsArray = Object.keys(watchlistSymbols).filter(symbol => watchlistSymbols[symbol]);
            sendDataToServer({
                type: 'watchlist',
                watchlistSymbols: watchlistSymbolsArray,
            });
        } else {
            // When switching to 'All Symbols', open the dropdown for the category saved in localStorage
            const savedCategory = localStorage.getItem('symbolCategory');
            if (savedCategory) {
                setSymbolCategory(savedCategory);
                const restored = symbolCategories.find(category => category.name === savedCategory);
                if (restored) {
                    setSymbolCategoryId(restored.id);
                    sendDataToServer({type: 'SymbolsPagination', paginationStart: 0, paginationEnd: 100, category: restored.id});
                }
            } else {
                setSymbolCategory(null);
            }
            setOpenCategories(new Set()); // Clear open categories set
        }
    };

    const categoriesToRender = useMemo(() => {
        const cats = symbolCategories || [];
        if (activeMode === 'watchlists') {
            return cats.filter((cat) => {
                return Object.entries(symbolData || {}).some(([sym, data]) => {
                    if (!watchlistSymbols[sym]) return false;
                    if (Number(data?.symbol_category) !== Number(cat.id)) return false;
                    if (searchInput && searchInput.trim().length > 0) {
                        return filteredSymbolSet.has(sym);
                    }
                    return true;
                });
            });
        }

        if (searchInput && searchInput.trim().length > 0) {
            return cats.filter((cat) => {
                return Object.entries(symbolData || {}).some(([sym, data]) => {
                    return Number(data?.symbol_category) === Number(cat.id) && filteredSymbolSet.has(sym);
                });
            });
        }

        return cats;
    }, [symbolCategories, activeMode, symbolData, watchlistSymbols, searchInput, filteredSymbolSet]);

    const hasWatchlistItems = useMemo(
        () => Object.values(watchlistSymbols || {}).some(Boolean),
        [watchlistSymbols],
    );


    return (
        <div className='global-platfrom mobile-styled' style={{ height: 'calc(100% - 70px)' }}>
            {/* Tab section */}
            <div className="symbol-mode-tabs global-platfrom ">
                <TabIndicator activeKey={activeMode} />
                <button
                    data-active={activeMode === 'symbols'}
                    className={`mode-tab-button ${activeMode === 'symbols' ? 'mode-tab-active' : ''}`}
                    onClick={() => {
                        handleModeClick('symbols');
                        sendDataToServer({type: 'selectedSymbolChange',selectedSymbolName: selectedAuthSymbol,selectedSymbolTimeFrame: selectedTimeFrame,selectedCategory: authSelectedCategory});
                    }}
                >
                    {t('symbol.allSymbols')}
                </button>
                <button
                    data-active={activeMode === 'watchlists'}
                    className={`mode-tab-button ${activeMode === 'watchlists' ? 'mode-tab-active' : ''}`}
                    onClick={() => {
                        handleModeClick('watchlists');
                    }}
                    disabled={!isWebSocketRefetch}
                >
                    {t('symbol.watchLists')}
                </button>
            </div>
            <div className={`Symbol ${SymbolPanelClass} symbol-panel-parent`}>
                <div className="Symbol global-platfrom symbol-panel-height" >
                    {/* Search input */}
                    <div className="search-container">
                        <FaSearch className="search-icon" />
                        <input
                            type="text"
                            placeholder={t('symbol.searchPlaceholder')}
                            disabled={!isWebSocketRefetch}
                            value={searchInput}
                            onKeyDown={handleSearchKeyDown}
                            onChange={(e) =>handleSearchChange(e.target.value)}
                            className='input-symbol-field'
                        />
                        {searchInput && (
                            <button type="button" className="clear-icon-symbol" aria-label="Clear symbol search"
                                disabled={!isWebSocketRefetch}
                                onClick={() => handleSearchChange('')}><FaTimes aria-hidden="true" /></button>
                        )}
                    </div>
                    <div className=''>
                        <div className="Symbol-table-head">
                            <div className="Symbol-table-thead">
                                <div className='star-th'></div>
                                <div className='session-th'></div>
                                <div className='symbol-th'>{t('tables.symbol')}</div>
                                <div className='bid-th'>{t('symbol.bid')}</div>
                                <div className='ask-th'>{t('symbol.ask')}</div>
                                <div className='spread-th'>{t('symbol.spread', 'Spread')}</div>
                            </div>
                        </div>
                    </div>

                    <div className="Symbol-list" ref={listRef}>
                        {loadingSymbolContext && <PanelSkeleton variant="symbols" rows={7} />}
                        {!loadingSymbolContext && searchInput && categoriesToRender.length === 0 && <div className="symbol-empty"><NoRecordsMessage variant="symbols" /></div>}

                        {(!loadingSymbolContext ? categoriesToRender : [])?.map((category) => {
                            const categorySymbolEntries = categoryEntries.get(String(category.id)) || [];
                            return (
                            searchInput.length === 0 ?
                            <wrapper key={category.id}>
                                <heading role="button" tabIndex={0} aria-expanded={symbolCategory === category.name}
                                    onKeyDown={handleCategoryKeyDown} onClick={animateCategoryClick(() => changeSymbolCategory(category.name, symbolCategory === category.name))} >
                                    <div className='indicator' >
                                    <svg className="symbol-category-chevron" viewBox="0 0 16 16" aria-hidden="true" style={{
                                        transform: symbolCategory === category.name ? 'rotate(90deg)' : 'none',
                                    }} >
                                    <path d="m6 3 5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    </div>
                                    {translateCategoryName(category.name, t)}{searchInput && <span className="symbol-result-count">{filteredSymbols.filter(name => Number(symbolData[name]?.symbol_category) === Number(category.id) && (activeMode !== 'watchlists' || watchlistSymbols[name])).length}</span>}
                                </heading>

                                <AnimatedSymbolGroup open={symbolCategory === category.name}><div
                                        className="Symbol-list-virtual"
                                        style={{
                                            height: getVirtualListHeight(categorySymbolEntries)
                                        }}
                                    >
                                        <AutoSizer>
                                            {({ height, width }) => (
                                            <List ref={instance => { if (instance) virtualLists.current.set(category.id, instance); else virtualLists.current.delete(category.id); }}
                                            className="symbol-react-window-list"
                                            key={`${symbolCategoryId}-${paginationStart}`}
                                            height={height}
                                            width={width}
                                            itemCount={categorySymbolEntries.length}
                                            itemSize={(index) => {
                                                const row = categorySymbolEntries[index];
                                                return row ? getSymbolRowHeight(row[1]) : SYMBOL_ROW_HEIGHT_WITH_DESC;
                                            }}
                                            estimatedItemSize={38}
                                            itemKey={(index) => categorySymbolEntries[index]?.[0] ?? index}
                                            onItemsRendered={({ visibleStartIndex, visibleStopIndex }) => {
                                                if (activeMode !== 'symbols' || symbolCategory !== category.name) return;
                                                sendDataToServer({
                                                    type: 'SymbolsPagination',
                                                    paginationStart: visibleStartIndex,
                                                    paginationEnd: visibleStopIndex+5,
                                                    category: symbolCategoryId,
                                                });

                                            }}
                                         itemData={({ index, style }) => {
                                                const [symbol, data] = categorySymbolEntries[index] || [];
                                                const isSelected = selectedAuthSymbol === symbol;

                                                if (!data) return null;

                                                return (
                                                    <div
                                                        onClick={() => handleRowClick(symbol, symbolData)}
                                                        className={`Symbol-tr ${!hasSymbolDescription(data) ? 'symbol-row-no-desc' : ''} ${isSelected ? 'selected-row' : ''} ${keyboardSymbol === symbol ? 'keyboard-row' : ''}`}
                                                        style={{
                                                            ...style,
                                                            display: 'flex',
                                                            alignItems: hasSymbolDescription(data) ? 'flex-start' : 'center',
                                                            width: '100%'
                                                        }}
                                                    >
                                                        <StarSvg
                                                            handleTdClick={handleTdClick}
                                                            symbol={symbol}
                                                            watchlistSymbols={watchlistSymbols[symbol] || false}
                                                        >
                                                            {t('symbol.addToWatchlist')}
                                                        </StarSvg>

                                                        <div className={`specific-td session-td`}>
                                                            {pendingSymbol === symbol ? <span className="symbol-loading" role="status" aria-label="Loading chart" /> : data.is_session_active === 0 ? (
                                                                <span className="symbol-session-dot" role="img" aria-label={t('symbol.marketClosed')} data-tooltip={t('symbol.marketClosed')} style={{ backgroundColor: selectedStyle.sellColor }} />
                                                            ) : (
                                                                <span className="symbol-session-dot" role="img" aria-label={t('symbol.marketOpen')} data-tooltip={t('symbol.marketOpen')} style={{ backgroundColor: selectedStyle.buyColor }} />
                                                            )}
                                                        </div>
                                                        <div
                                                            className="Symbol-td symbol-td enclp2 symbol-name-cell"
                                                            data-tooltip={!isMobile && symbol.length > 7 ? symbol : undefined}
                                                            style={{
                                                                position: 'relative',
                                                                isolation: 'isolate',
                                                            }}
                                                        >
                                                            <Ripple />
                                                            <div className='symbol-name-text'>{symbol}</div>
                                                        </div>
                                                        <div
                                                            className={`Symbol-td ${data.bid_class} bid-td`}
                                                            style={{
                                                                color:
                                                                data.bid_class === 'Symbol-price-up'
                                                                    ? selectedStyle.buyColor
                                                                    : data.bid_class === 'Symbol-price-down'
                                                                    ? selectedStyle.sellColor
                                                                    : 'var(--content-primary)',
                                                                position: 'relative',
                                                                overflow: 'hidden',
                                                                isolation: 'isolate',
                                                                textOverflow: 'ellipsis',

                                                            }}
                                                        >
                                                            <Ripple />
                                                            {displayQuote(data.bid)}
                                                        </div>
                                                        <div
                                                            className={`Symbol-td ${data.ask_class} ask-td`}
                                                            data-full-text={data.ask ?? '-'}
                                                            style={{
                                                                color:
                                                                data.ask_class === 'Symbol-price-up'
                                                                    ? selectedStyle.buyColor
                                                                    : data.ask_class === 'Symbol-price-down'
                                                                    ? selectedStyle.sellColor
                                                                    : 'var(--content-primary)',
                                                                position: 'relative',
                                                                overflow: 'hidden',
                                                                isolation: 'isolate',
                                                                textOverflow: 'ellipsis',

                                                            }}
                                                        >
                                                            <Ripple />
                                                            {displayQuote(data.ask)}
                                                        </div>
                                                            <div className="Symbol-td spread-td" title={formatSpread(data)}>{formatSpread(data)}</div>
                                                        {data.description ? (
                                                            <div
                                                                className='symbol-row-description'
                                                                data-tooltip={getDescriptionTooltip(data.description) || undefined}
                                                            >
                                                                {data.description}
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                );
                                            }}>
                                            {StableSymbolRow}
                                        </List>
                                            )}
                                        </AutoSizer>
                                    </div></AnimatedSymbolGroup>
                            </wrapper> :
                            <wrapper key={category.id}>
                                <heading role="button" tabIndex={0} aria-expanded={!closedSearchCategories.has(category.name)}
                                    onKeyDown={handleCategoryKeyDown} onClick={animateCategoryClick(() => toggleSearchCategory(category.name))}>
                                    <div className='indicator' >
                                        <svg className="symbol-category-chevron" viewBox="0 0 16 16" aria-hidden="true" style={{
                                            transform: closedSearchCategories.has(category.name) ? 'none' : 'rotate(90deg)',
                                        }}>
                                            <path d="m6 3 5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </div>
                                    {translateCategoryName(category.name, t)}{searchInput && <span className="symbol-result-count">{filteredSymbols.filter(name => Number(symbolData[name]?.symbol_category) === Number(category.id) && (activeMode !== 'watchlists' || watchlistSymbols[name])).length}</span>}
                                </heading>

                                {/* Use the SAME virtual list renderer during search for consistent layout */}
                                <AnimatedSymbolGroup open={!closedSearchCategories.has(category.name)}><div
                                    className="Symbol-list-virtual"
                                    style={{
                                        height: getVirtualListHeight(categorySymbolEntries)
                                    }}
                                >
                                    <AutoSizer>
                                        {({ height, width }) => (
                                            <List ref={instance => { if (instance) virtualLists.current.set(category.id, instance); else virtualLists.current.delete(category.id); }}
                                                className="symbol-react-window-list"
                                                height={height}
                                                width={width}
                                                itemCount={categorySymbolEntries.length}
                                                itemSize={(index) => {
                                                    const row = categorySymbolEntries[index];
                                                    return row ? getSymbolRowHeight(row[1]) : SYMBOL_ROW_HEIGHT_WITH_DESC;
                                                }}
                                                estimatedItemSize={38}
                                                itemKey={(index) => categorySymbolEntries[index]?.[0] ?? index}
                                             itemData={({ index, style }) => {
                                                    const [sym, d] = categorySymbolEntries[index] || [];
                                                    const isSelected = selectedAuthSymbol === sym;
                                                    if (!d) return null;

                                                    return (
                                                        <div
                                                            onClick={() => handleRowClick(sym, symbolData)}
                                                            className={`Symbol-tr ${!hasSymbolDescription(d) ? 'symbol-row-no-desc' : ''} ${isSelected ? 'selected-row' : ''} ${keyboardSymbol === sym ? 'keyboard-row' : ''}`}
                                                            style={{
                                                                ...style,
                                                                display: 'flex',
                                                                alignItems: hasSymbolDescription(d) ? 'flex-start' : 'center',
                                                                width: '100%'
                                                            }}
                                                        >
                                                            <StarSvg
                                                                handleTdClick={handleTdClick}
                                                                symbol={sym}
                                                                watchlistSymbols={watchlistSymbols[sym] || false}
                                                            >
                                                                {t('symbol.addToWatchlist')}
                                                            </StarSvg>

                                                            <div className={`specific-td session-td`}>
                                                                {pendingSymbol === sym ? <span className="symbol-loading" role="status" aria-label="Loading chart" /> : d.is_session_active === 0 ? (
                                                                    <span className="symbol-session-dot" role="img" aria-label={t('symbol.marketClosed')} data-tooltip={t('symbol.marketClosed')} style={{ backgroundColor: selectedStyle.sellColor }} />
                                                                ) : (
                                                                    <span className="symbol-session-dot" role="img" aria-label={t('symbol.marketOpen')} data-tooltip={t('symbol.marketOpen')} style={{ backgroundColor: selectedStyle.buyColor }} />
                                                                )}
                                                            </div>

                                                            <div
                                                                className="Symbol-td symbol-td enclp2 symbol-name-cell"
                                                                data-tooltip={!isMobile && sym.length > 7 ? sym : undefined}
                                                                style={{
                                                                    position: 'relative',
                                                                    isolation: 'isolate',
                                                                }}
                                                            >
                                                                <Ripple />
                                                                <div className='symbol-name-text'>{sym}</div>
                                                            </div>

                                                            <div
                                                                className={`Symbol-td ${d.bid_class} bid-td`}
                                                                style={{
                                                                    color:
                                                                        d.bid_class === 'Symbol-price-up'
                                                                            ? selectedStyle.buyColor
                                                                            : d.bid_class === 'Symbol-price-down'
                                                                            ? selectedStyle.sellColor
                                                                            : 'var(--content-primary)',
                                                                    position: 'relative',
                                                                    overflow: 'hidden',
                                                                    isolation: 'isolate',
                                                                    textOverflow: 'ellipsis',
                                                                }}
                                                            >
                                                                <Ripple />
                                                                {displayQuote(d.bid)}
                                                            </div>

                                                            <div
                                                                className={`Symbol-td ${d.ask_class} ask-td`}
                                                                data-full-text={d.ask ?? '-'}
                                                                style={{
                                                                    color:
                                                                        d.ask_class === 'Symbol-price-up'
                                                                            ? selectedStyle.buyColor
                                                                            : d.ask_class === 'Symbol-price-down'
                                                                            ? selectedStyle.sellColor
                                                                            : 'var(--content-primary)',
                                                                    position: 'relative',
                                                                    overflow: 'hidden',
                                                                    isolation: 'isolate',
                                                                    textOverflow: 'ellipsis',
                                                                }}
                                                            >
                                                                <Ripple />
                                                                {displayQuote(d.ask)}
                                                            </div>
                                                            <div className="Symbol-td spread-td" title={formatSpread(d)}>{formatSpread(d)}</div>

                                                            {d.description ? (
                                                                <div
                                                                    className='symbol-row-description'
                                                                    data-tooltip={getDescriptionTooltip(d.description) || undefined}
                                                                >
                                                                    {d.description}
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                    );
                                                }}>
                                                {StableSymbolRow}
                                            </List>
                                        )}
                                    </AutoSizer>
                                </div></AnimatedSymbolGroup>
</wrapper>
                        );
                        })}
                    </div>
                </div>
            </div>
            <div className={`order-book-wrapper ${WatchListClass}`}>
                <div className="Symbol global-platfrom symbol-panel-height" style={{ height: '100%' }} >
                    {/* Search input */}
                    <div className="search-container">
                        <FaSearch className="search-icon" />
                        <input
                            type="text"
                            placeholder={t('symbol.searchPlaceholder')}
                            value={searchInput}
                            onKeyDown={handleSearchKeyDown}
                            disabled={!isWebSocketRefetch}
                            onChange={(e) =>handleSearchChange(e.target.value)}
                            className='input-symbol-field'
                        />
                        {searchInput && (
                            <button type="button" className="clear-icon-symbol" aria-label="Clear symbol search"
                                disabled={!isWebSocketRefetch}
                                onClick={() => handleSearchChange('')}><FaTimes aria-hidden="true" /></button>
                        )}
                    </div>

                    {/* WATCH LIST TAB  */}

                    {(hasWatchlistItems || !!searchInput) && (
                    <div className=''>
                        <div className="Symbol-table-head">
                            <div className="Symbol-table-thead">
                                <div className='star-th'></div>
                                <div className='session-th'></div>
                                <div className='symbol-th'>{t('tables.symbol')}</div>
                                <div className='bid-th'>{t('symbol.bid')}</div>
                                <div className='ask-th'>{t('symbol.ask')}</div>
                                <div className='spread-th'>{t('symbol.spread', 'Spread')}</div>
                            </div>
                        </div>
                    </div>
                    )}

                    <div
                        className={`Symbol-list${!loadingSymbolContext && !searchInput && !hasWatchlistItems ? ' Symbol-list--watchlist-empty' : ''}`}
                    >
                        {loadingSymbolContext && <PanelSkeleton variant="symbols" rows={7} />}
                        {!loadingSymbolContext && !searchInput && !hasWatchlistItems && (
                            <div className="watchlist-empty" role="status">
                                <div className="watchlist-empty-icon" aria-hidden="true">
                                    <svg
                                        className="watchlist-empty-svg"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                    >
                                        <path
                                            d="M4 1.5h8v13L8 11.35 4 14.5V1.5z"
                                            stroke="currentColor"
                                            strokeWidth="1.35"
                                            strokeLinejoin="round"
                                            strokeLinecap="round"
                                        />
                                    </svg>
                                </div>
                                <p className="watchlist-empty-title">{t('symbol.watchlistEmptyTitle')}</p>
                                <p className="watchlist-empty-hint">{t('symbol.watchlistEmptyHint')}</p>
                            </div>
                        )}
                        {!loadingSymbolContext && searchInput && categoriesToRender.length === 0 && <div className="symbol-empty"><NoRecordsMessage variant="symbols" /></div>}
                        {(!loadingSymbolContext ? categoriesToRender : [])?.map((category) => {
                            const categoryWatchlistEntries = (categoryEntries.get(String(category.id)) || []).filter(([sym]) => watchlistSymbols[sym]);
                            return (
                            searchInput.length === 0 ?
                            <wrapper key={category.id}>
                                <heading role="button" tabIndex={0} aria-expanded={activeMode === 'watchlists' ? openCategories.has(category.name) : symbolCategory === category.name}
                                    onKeyDown={handleCategoryKeyDown} onClick={animateCategoryClick(() => {
                                    if (activeMode === 'watchlists') {
                                        toggleWatchlistCategory(category.name);
                                    } else {
                                        changeSymbolCategory(category.name, symbolCategory === category.name ? true : false);
                                    }
                                })} >
                                    <div className='indicator' >
                                    <svg className="symbol-category-chevron" viewBox="0 0 16 16" aria-hidden="true" style={{
                                        transform: activeMode === 'watchlists'
                                            ? (openCategories.has(category.name) ? 'rotate(90deg)' : 'none')
                                            : (symbolCategory === category.name ? 'rotate(90deg)' : 'none'),
                                    }}>
                                    <path d="m6 3 5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    </div>
                                    {translateCategoryName(category.name, t)}{searchInput && <span className="symbol-result-count">{filteredSymbols.filter(name => Number(symbolData[name]?.symbol_category) === Number(category.id) && (activeMode !== 'watchlists' || watchlistSymbols[name])).length}</span>}
                                </heading>

                                <AnimatedSymbolGroup open={activeMode === 'watchlists' ? openCategories.has(category.name) : symbolCategory === category.name}><div
                                        className="Symbol-list-virtual"
                                        style={{
                                            height: getVirtualListHeight(categoryWatchlistEntries)
                                        }}
                                    >
                                        <AutoSizer>
                                            {({ height, width }) => (
                                                <List ref={instance => { if (instance) virtualLists.current.set(category.id, instance); else virtualLists.current.delete(category.id); }}
                                                    className="symbol-react-window-list"
                                                    height={height}
                                                    width={width}
                                                    itemCount={categoryWatchlistEntries.length}
                                                    itemSize={(index) => {
                                                        const row = categoryWatchlistEntries[index];
                                                        return row ? getSymbolRowHeight(row[1]) : SYMBOL_ROW_HEIGHT_WITH_DESC;
                                                    }}
                                                    estimatedItemSize={38}
                                                    itemKey={(index) => categoryWatchlistEntries[index]?.[0] ?? index}
                                                 itemData={({ index, style }) => {
                                                        const [sym, d] = categoryWatchlistEntries[index] || [];
                                                        const isSelected = selectedAuthSymbol === sym;
                                                        if (!d) return null;

                                                        return (
                                                            <div
                                                                onClick={() => handleRowClick(sym, symbolData)}
                                                            className={`Symbol-tr ${!hasSymbolDescription(d) ? 'symbol-row-no-desc' : ''} ${isSelected ? 'selected-row' : ''} ${keyboardSymbol === sym ? 'keyboard-row' : ''}`}
                                                                style={{
                                                                    ...style,
                                                                    display: 'flex',
                                                                    alignItems: hasSymbolDescription(d) ? 'flex-start' : 'center',
                                                                    width: '100%'
                                                                }}
                                                            >
                                                                <StarSvg
                                                                    handleTdClick={handleTdClick}
                                                                    symbol={sym}
                                                                    watchlistSymbols={watchlistSymbols[sym] || false}
                                                                >
                                                                    {t('symbol.removeFromWatchlist')}
                                                                </StarSvg>

                                                                <div className={`specific-td session-td`}>
                                                                    {pendingSymbol === sym ? <span className="symbol-loading" role="status" aria-label="Loading chart" /> : d.is_session_active === 0 ? (
                                                                        <span className="symbol-session-dot" role="img" aria-label={t('symbol.marketClosed')} data-tooltip={t('symbol.marketClosed')} style={{ backgroundColor: selectedStyle.sellColor }} />
                                                                    ) : (
                                                                        <span className="symbol-session-dot" role="img" aria-label={t('symbol.marketOpen')} data-tooltip={t('symbol.marketOpen')} style={{ backgroundColor: selectedStyle.buyColor }} />
                                                                    )}
                                                                </div>

                                                                <div
                                                                    className="Symbol-td symbol-td enclp2 symbol-name-cell"
                                                                    data-tooltip={!isMobile && sym.length > 7 ? sym : undefined}
                                                                    style={{
                                                                        position: 'relative',
                                                                        isolation: 'isolate',
                                                                    }}
                                                                >
                                                                    <Ripple />
                                                                    <div className='symbol-name-text'>{sym}</div>
                                                                </div>

                                                                <div
                                                                    className={`Symbol-td ${d.bid_class} bid-td`}
                                                                    style={{
                                                                        color:
                                                                            d.bid_class === 'Symbol-price-up'
                                                                                ? selectedStyle.buyColor
                                                                                : d.bid_class === 'Symbol-price-down'
                                                                                ? selectedStyle.sellColor
                                                                                : 'var(--content-primary)',
                                                                        position: 'relative',
                                                                        overflow: 'hidden',
                                                                        isolation: 'isolate',
                                                                        textOverflow: 'ellipsis',
                                                                    }}
                                                                >
                                                                    <Ripple />
                                                                    {displayQuote(d.bid)}
                                                                </div>

                                                                <div
                                                                    className={`Symbol-td ${d.ask_class} ask-td`}
                                                                    data-full-text={d.ask ?? '-'}
                                                                    style={{
                                                                        color:
                                                                            d.ask_class === 'Symbol-price-up'
                                                                                ? selectedStyle.buyColor
                                                                                : d.ask_class === 'Symbol-price-down'
                                                                                ? selectedStyle.sellColor
                                                                                : 'var(--content-primary)',
                                                                        position: 'relative',
                                                                        overflow: 'hidden',
                                                                        isolation: 'isolate',
                                                                        textOverflow: 'ellipsis',
                                                                    }}
                                                                >
                                                                    <Ripple />
                                                                    {displayQuote(d.ask)}
                                                                </div>
                                                            <div className="Symbol-td spread-td" title={formatSpread(d)}>{formatSpread(d)}</div>

                                                                {d.description ? (
                                                                    <div
                                                                        className='symbol-row-description'
                                                                        data-tooltip={getDescriptionTooltip(d.description) || undefined}
                                                                    >
                                                                        {d.description}
                                                                    </div>
                                                                ) : null}
                                                            </div>
                                                        );
                                                    }}>
                                                    {StableSymbolRow}
                                                </List>
                                            )}
                                        </AutoSizer>
                                    </div></AnimatedSymbolGroup>
                            </wrapper> :
                            <wrapper>
                                <heading role="button" tabIndex={0} aria-expanded={!closedSearchCategories.has(category.name)}
                                    onKeyDown={handleCategoryKeyDown} onClick={animateCategoryClick(() => toggleSearchCategory(category.name))}>
                                    <div className='indicator' >
                                    <svg className="symbol-category-chevron" viewBox="0 0 16 16" aria-hidden="true" style={{
                                        transform: closedSearchCategories.has(category.name) ? 'none' : 'rotate(90deg)',
                                        }}>
                                    <path d="m6 3 5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    </div>
                                    {translateCategoryName(category.name, t)}{searchInput && <span className="symbol-result-count">{filteredSymbols.filter(name => Number(symbolData[name]?.symbol_category) === Number(category.id) && (activeMode !== 'watchlists' || watchlistSymbols[name])).length}</span>}
                                </heading>
                                {/* Use the SAME virtual list renderer during search for consistent layout */}
                                <AnimatedSymbolGroup open={!closedSearchCategories.has(category.name)}><div
                                    className="Symbol-list-virtual"
                                    style={{
                                        height: getVirtualListHeight(categoryWatchlistEntries)
                                    }}
                                >
                                    <AutoSizer>
                                        {({ height, width }) => (
                                            <List ref={instance => { if (instance) virtualLists.current.set(category.id, instance); else virtualLists.current.delete(category.id); }}
                                                className="symbol-react-window-list"
                                                height={height}
                                                width={width}
                                                itemCount={categoryWatchlistEntries.length}
                                                itemSize={(index) => {
                                                    const row = categoryWatchlistEntries[index];
                                                    return row ? getSymbolRowHeight(row[1]) : SYMBOL_ROW_HEIGHT_WITH_DESC;
                                                }}
                                                estimatedItemSize={38}
                                                itemKey={(index) => categoryWatchlistEntries[index]?.[0] ?? index}
                                             itemData={({ index, style }) => {
                                                    const [sym, d] = categoryWatchlistEntries[index] || [];
                                                    const isSelected = selectedAuthSymbol === sym;
                                                    if (!d) return null;

                                                    return (
                                                        <div
                                                            onClick={() => handleRowClick(sym, symbolData)}
                                                            className={`Symbol-tr ${!hasSymbolDescription(d) ? 'symbol-row-no-desc' : ''} ${isSelected ? 'selected-row' : ''} ${keyboardSymbol === sym ? 'keyboard-row' : ''}`}
                                                            style={{
                                                                ...style,
                                                                display: 'flex',
                                                                alignItems: hasSymbolDescription(d) ? 'flex-start' : 'center',
                                                                width: '100%'
                                                            }}
                                                        >
                                                            <StarSvg
                                                                handleTdClick={handleTdClick}
                                                                symbol={sym}
                                                                watchlistSymbols={watchlistSymbols[sym] || false}
                                                            >
                                                                {t('symbol.removeFromWatchlist')}
                                                            </StarSvg>

                                                            <div className={`specific-td session-td`}>
                                                                {pendingSymbol === sym ? <span className="symbol-loading" role="status" aria-label="Loading chart" /> : d.is_session_active === 0 ? (
                                                                    <span className="symbol-session-dot" role="img" aria-label={t('symbol.marketClosed')} data-tooltip={t('symbol.marketClosed')} style={{ backgroundColor: selectedStyle.sellColor }} />
                                                                ) : (
                                                                    <span className="symbol-session-dot" role="img" aria-label={t('symbol.marketOpen')} data-tooltip={t('symbol.marketOpen')} style={{ backgroundColor: selectedStyle.buyColor }} />
                                                                )}
                                                            </div>

                                                            <div
                                                                className="Symbol-td symbol-td enclp2 symbol-name-cell"
                                                                data-tooltip={!isMobile && sym.length > 7 ? sym : undefined}
                                                                style={{
                                                                    position: 'relative',
                                                                    isolation: 'isolate',
                                                                }}
                                                            >
                                                                <Ripple />
                                                                <div className='symbol-name-text'>{sym}</div>
                                                            </div>

                                                            <div
                                                                className={`Symbol-td ${d.bid_class} bid-td`}
                                                                style={{
                                                                    color:
                                                                        d.bid_class === 'Symbol-price-up'
                                                                            ? selectedStyle.buyColor
                                                                            : d.bid_class === 'Symbol-price-down'
                                                                            ? selectedStyle.sellColor
                                                                            : 'var(--content-primary)',
                                                                    position: 'relative',
                                                                    overflow: 'hidden',
                                                                    isolation: 'isolate',
                                                                    textOverflow: 'ellipsis',
                                                                }}
                                                            >
                                                                <Ripple />
                                                                {displayQuote(d.bid)}
                                                            </div>

                                                            <div
                                                                className={`Symbol-td ${d.ask_class} ask-td`}
                                                                data-full-text={d.ask ?? '-'}
                                                                style={{
                                                                    color:
                                                                        d.ask_class === 'Symbol-price-up'
                                                                            ? selectedStyle.buyColor
                                                                            : d.ask_class === 'Symbol-price-down'
                                                                            ? selectedStyle.sellColor
                                                                            : 'var(--content-primary)',
                                                                    position: 'relative',
                                                                    overflow: 'hidden',
                                                                    isolation: 'isolate',
                                                                    textOverflow: 'ellipsis',
                                                                }}
                                                            >
                                                                <Ripple />
                                                                {displayQuote(d.ask)}
                                                            </div>
                                                            <div className="Symbol-td spread-td" title={formatSpread(d)}>{formatSpread(d)}</div>

                                                            {d.description ? (
                                                                <div
                                                                    className='symbol-row-description'
                                                                    data-tooltip={getDescriptionTooltip(d.description) || undefined}
                                                                >
                                                                    {d.description}
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                    );
                                                }}>
                                                {StableSymbolRow}
                                                </List>
                                        )}
                                    </AutoSizer>
                                </div></AnimatedSymbolGroup>
</wrapper>
                        );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Symbol;
